import axios from 'axios';

import { SubscriptionTier } from '@invoice-simple/common';
import { ISPaywallCoupon } from '@invoice-simple/is-coupon';

import type { AppStore } from '../stores/AppStore';
import { trackDataLayerEvent } from 'src/analytics/google';
import { SubTax } from 'src/apis/avalara';
import { Cadence } from 'src/models/UserModel';
import { trackOptimizelyWebExp } from 'src/services/flags/utils/track';
import localeStore from 'src/stores/LocaleStore';
import environmentStore from '../stores/EnvironmentStore';
import { removeUndefinedOrNull } from './functions';

type GetFeatureListImageUrlParams = {
  tier: SubscriptionTier | undefined;
  language: string;
  couponName?: ISPaywallCoupon;
};

function getStripeMetadata({
  origin,
  store,
  orderSku,
  couponSku,
  couponName,
  tier,
  cadence,
  toBeTransferred,
  source,
  tax
}: {
  origin?: string;
  store: AppStore;
  orderSku: string;
  couponSku?: string;
  couponName?: ISPaywallCoupon;
  tier?: SubscriptionTier;
  cadence?: Cadence;
  toBeTransferred?: boolean;
  source?: string;
  tax?: SubTax;
}) {
  const { user } = store;
  return {
    userId: user.id,
    userEmail: user.email,
    accountId: user.accountId,
    orderSku,
    couponSku,
    couponName,
    source,
    referralCode: user.advocateReferralCode,
    currencyCode: user.geoSubCurrencyCode,
    app: environmentStore.appHost,
    ...store.getSubscriptionEventData({ tier, cadence, couponSku }),
    ...removeUndefinedOrNull({
      origin,
      isApp: environmentStore.appHost,
      isPlatform: 'web',
      isCountry: user.countryCode,
      isVersion: environmentStore.getReactAppVersion(),
      isAccount: user.accountId,
      isUser: user.id,
      isInstallation: environmentStore.installationId,
      parseSessionToken: user.sessionToken,
      toBeTransferred,
      taxRate: tax?.taxRate,
      taxTotal: tax?.taxTotal
    })
  };
}

interface CheckoutSessionRequestBody {
  productTitle: string;
  description?: string;
  price: number;
  currency: string;
  locale: string;
  featureListImageUrl: string;
  userEmail: string | undefined;
  isGuestUser: boolean;
  stripeMetadata: any;
  successUrl?: string;
  cancelUrl?: string;
}

interface CheckoutConfig {
  store: AppStore;
  productTitle: string;
  description?: string;
  price: number;
  orderSku: string;
  successUrl?: string;
  cancelUrl?: string;
  couponSku?: string;
  couponName?: ISPaywallCoupon;
  tier?: SubscriptionTier;
  cadence?: Cadence;
  origin?: string;
  toBeTransferred?: boolean;
  tax?: SubTax;
}

function getFeatureListImageUrl({ tier, language, couponName }: GetFeatureListImageUrlParams) {
  const couponImageSuffixes: Partial<Record<ISPaywallCoupon, string>> = {
    referral50off3months: '50off3months'
  };

  const availableLanguages = ['en', 'es', 'fr', 'de', 'it', 'nl'];
  const languageCode = availableLanguages.includes(language) ? language : 'en';
  const imageName = tier ? `${tier}-${languageCode}` : languageCode;
  const image =
    couponName && couponImageSuffixes[couponName]
      ? `${imageName}-${couponImageSuffixes[couponName]}`
      : imageName;
  return `${environmentStore.cdnURL}/images/feature-list/${image}.png`;
}

function getCheckoutSessionRequestBody(checkoutConfig: CheckoutConfig): CheckoutSessionRequestBody {
  const {
    store,
    productTitle,
    description,
    price,
    orderSku,
    successUrl,
    cancelUrl,
    couponSku,
    couponName,
    tier,
    cadence,
    origin,
    toBeTransferred,
    tax
  } = checkoutConfig;
  const { user } = store;
  const locale = localeStore.currentLocaleCountryCode;

  const featureListImageUrl = getFeatureListImageUrl({
    tier,
    couponName,
    language: locale.toLowerCase() || 'en'
  });

  return {
    productTitle,
    description,
    price,
    currency: user.geoSubCurrencyCode,
    locale,
    featureListImageUrl,
    userEmail: user.email,
    isGuestUser: user.isGuest,
    successUrl,
    cancelUrl,
    stripeMetadata: getStripeMetadata({
      origin,
      store,
      orderSku,
      couponSku,
      couponName,
      tier,
      cadence,
      toBeTransferred,
      source: user.accountSource,
      tax
    })
  };
}

export async function redirectToStripeCheckout(checkoutConfig: CheckoutConfig) {
  const { store, tier, cadence, couponSku, price, orderSku } = checkoutConfig;

  const response = await axios.post(
    '/stripe/sessions',
    getCheckoutSessionRequestBody(checkoutConfig)
  );
  const url = response?.data?.url;
  if (url) {
    const subscriptionEventData = store.getSubscriptionEventData({
      tier,
      cadence,
      couponSku,
      orderSku
    });
    await store.user.events.trackAction('subscription-checkout', subscriptionEventData);
    trackOptimizelyWebExp('subscription-checkout');
    trackDataLayerEvent('subscription-checkout', {
      plan_name: tier,
      plan_type: cadence,
      plan_price: price / 100,
      new_subscription: store.user.hadPaidSubsInThePast ? 'no' : 'yes'
    });

    window.location.assign(url);
  } else {
    throw new Error('Checkout URL is undefined');
  }
}

interface StripeCustomer {
  name: string;
  email: string;
}

export async function getStripeCustomerFromSession(sessionId: string): Promise<StripeCustomer> {
  const response = await axios.get<StripeCustomer>(`/stripe/sessions/${sessionId}/customer`);
  return response.data;
}

interface StripeCheckoutSession {
  customer_details: {
    name: string;
    email: string;
  };
  metadata: {
    accountId: string;
    toBeTransferred?: boolean;
    couponSku?: string;
    couponName?: ISPaywallCoupon;
  };
}

export async function getStripeCheckoutSession(sessionId: string): Promise<StripeCheckoutSession> {
  const { data } = await axios.get<StripeCheckoutSession>(`/stripe/sessions/${sessionId}`);
  return data;
}
